import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Button,
  Tooltip,
  IconButton,
  Collapse,
  Badge,
  Chip,
  Avatar,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemAvatar
} from '@mui/material';
import {
  Add as AddIcon,
  Tag as TagIcon,
  ExpandLess,
  ExpandMore,
  Person as PersonIcon,
  Chat as ChatIcon,
  PersonAdd as PersonAddIcon,
  Notifications as NotificationsIcon,
  Lock as LockIcon,
  DragIndicator as DragIndicatorIcon,
  PersonRemove as PersonRemoveIcon,
  AdminPanelSettings as AdminIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../components/auth/AuthModule';
import UserSelectionDialog from './UserSelectionDialog';
import { message as messageApi, channel as channelApi } from '../../../utils/api';
import socketManager from '../../../utils/socketManager';
import { DragDropContext, Draggable } from '../../../components/shared/DragDropWrapper';
import { StrictModeDroppable } from '../../../components/shared/DragDropWrapper';
import { useTheme, useMediaQuery } from '@mui/material';

// Width of the sidebar
const SIDEBAR_WIDTH = 280;

// Admin Actions Menu dropdown component
const AdminActionsMenu = ({ onInviteClick, onRemoveClick }) => {
  const { t } = useTranslation(['common']);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleInviteOption = () => {
    handleClose();
    onInviteClick();
  };
  
  const handleRemoveOption = () => {
    handleClose();
    onRemoveClick();
  };
  
  return (
    <>
      <Button
        variant="outlined"
        size="medium"
        aria-controls={open ? 'admin-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        startIcon={<AdminIcon />}
        sx={{ 
          mt: 1, 
          borderRadius: 1,
          boxShadow: 1,
          height: '40px',
          minWidth: isMobile ? '40px' : '120px'
        }}
      >
        {!isMobile && t('admin.actions')}
      </Button>
      
      <Menu
        id="admin-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'admin-button',
        }}
      >
        <MenuItem onClick={handleInviteOption}>
          <ListItemIcon>
            <PersonAddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('comms.inviteUsers')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleRemoveOption}>
          <ListItemIcon>
            <PersonRemoveIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('comms.removeUser')}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

const ChannelSidebar = ({ 
  teamChannels = [], 
  privateChannels = [], 
  lockedChannels = [],
  selectedChannel, 
  onChannelClick, 
  onCreateChannelClick,
  onCreateDirectMessage,
  visible,
  isMobile,
  onChannelsReordered,
  onCloseSidebar,
  onInviteClick,
  onRemoveClick
}) => {
  const { t } = useTranslation(['common']);
  const { user } = useAuth();
  const [teamChannelsOpen, setTeamChannelsOpen] = useState(true);
  const [privateChannelsOpen, setPrivateChannelsOpen] = useState(true);
  const [lockedChannelsOpen, setLockedChannelsOpen] = useState(true);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const [isDraggable, setIsDraggable] = useState(false);
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [pressedChannelId, setPressedChannelId] = useState(null);

  // Fetch unread counts when channels or selected channel changes
  useEffect(() => {
    const fetchUnreadCounts = async () => {
      if (!user || !user._id) return;
      
      try {
        setIsLoading(true);
        const channelIds = [...teamChannels, ...privateChannels, ...lockedChannels].map(channel => channel._id);
        
        console.log('Channels to fetch unread counts for:', {
          teamChannels: teamChannels.length,
          privateChannels: privateChannels.length,
          lockedChannels: lockedChannels.length,
          totalIds: channelIds.length
        });
        
        if (channelIds.length > 0) {
          console.log('Fetching unread counts for channels:', channelIds);
          const response = await messageApi.getUnreadCounts(user._id, channelIds);
          console.log('Unread counts response RAW:', response);
          console.log('Unread counts response COUNTS:', response.counts);
          
          // Log each channel's mention count for debugging
          Object.keys(response.counts || {}).forEach(channelId => {
            const count = response.counts[channelId];
            console.log(`Channel ${channelId}: ${count.unreadMessages} unread, ${count.unreadMentions} mentions`);
          });
          
          // Update the state and force a repaint to ensure consistent UI across browsers
          setUnreadCounts(response.counts || {});
          
          // Force browser to repaint
          window.requestAnimationFrame(() => {
            const forceRepaint = document.body.offsetHeight;
          });
        }
      } catch (error) {
        console.error('Error fetching unread counts:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUnreadCounts();
    
    // Debug current unread counts after setting
    console.log('Current unread counts state:', unreadCounts);
  }, [user, teamChannels, privateChannels, lockedChannels]);
  
  // Listen for socket events that affect unread counts
  useEffect(() => {
    if (!user || !user._id) return;
    
    // New message received - update unread counts
    const handleNewMessage = (data) => {
      try {
        const { message } = data;
        if (!message || !message.channel) return;
        
        console.log('New message received in channel:', message.channel);
        console.log('Current selected channel:', selectedChannel?._id);
        console.log('Message contains mentions:', message.mentions);
        console.log('Current user ID:', user._id);
        
        // If message is from someone else and not in the currently selected channel
        if (
          message.sender._id !== user._id && 
          (!selectedChannel || selectedChannel._id !== message.channel)
        ) {
          console.log('Updating unread count for channel:', message.channel);
          
          // Check if user is mentioned
          const isMentioned = message.mentions && 
            message.mentions.some(m => 
              m._id === user._id || 
              String(m._id) === String(user._id)
            );
          
          console.log('Is user mentioned?', isMentioned);
          
          // Force a synchronous state update for consistency across browsers
          setUnreadCounts(prev => {
            const channelId = message.channel;
            const currentCount = prev[channelId] || { unreadMessages: 0, unreadMentions: 0 };
            
            const newCount = {
              ...prev,
              [channelId]: {
                unreadMessages: currentCount.unreadMessages + 1,
                unreadMentions: currentCount.unreadMentions + (isMentioned ? 1 : 0)
              }
            };
            
            console.log('Updated unread counts:', newCount);
            
            // Ensure Chrome updates the badge by using requestAnimationFrame
            window.requestAnimationFrame(() => {
              // This empty callback forces a repaint in Chrome
              const forceRepaint = document.body.offsetHeight;
            });
            
            return newCount;
          });
          
          // If this is a new message in a private channel, make sure the section is open
          const isDmChannel = privateChannels.some(channel => channel._id === message.channel);
          if (isDmChannel) {
            setPrivateChannelsOpen(true);
          }
        }
      } catch (error) {
        console.error('Error handling new message in sidebar:', error);
      }
    };
    
    // Direct mention received
    const handleMention = (data) => {
      try {
        console.log('MENTION EVENT RECEIVED:', data);
        
        const { message, channelId } = data;
        
        if (!message || !channelId) {
          console.error('Invalid mention data:', data);
          return;
        }
        
        console.log('Mention received in sidebar for channel:', channelId);
        console.log('Mentioned user:', user._id);
        console.log('Mentioned in message:', message._id);
        
        // If not in the currently selected channel
        if (!selectedChannel || selectedChannel._id !== channelId) {
          // Find the channel to determine if it's a team or private channel
          const channel = [...teamChannels, ...privateChannels, ...lockedChannels]
            .find(ch => ch._id === channelId);
            
          console.log('Found channel for mention:', channel?.name);
          
          // Force a synchronous state update for consistency across browsers
          setUnreadCounts(prev => {
            const currentCount = prev[channelId] || { unreadMessages: 0, unreadMentions: 0 };
            
            const newCount = {
              ...prev,
              [channelId]: {
                unreadMessages: currentCount.unreadMessages,
                unreadMentions: currentCount.unreadMentions + 1
              }
            };
            
            console.log('Updated unread mentions:', newCount[channelId]);
            console.log('FULL UNREAD COUNTS:', {...prev, [channelId]: newCount[channelId]});
            
            // Ensure Chrome updates the badge by using requestAnimationFrame
            window.requestAnimationFrame(() => {
              // This empty callback forces a repaint in Chrome
              const forceRepaint = document.body.offsetHeight;
            });
            
            return newCount;
          });
          
          // Auto-expand the appropriate section based on channel type
          if (channel) {
            if (channel.type === 'team') {
              setTeamChannelsOpen(true);
            } else if (channel.type === 'private') {
              setPrivateChannelsOpen(true);
            } else if (channel.isLocked) {
              setLockedChannelsOpen(true);
            }
          }
        }
      } catch (error) {
        console.error('Error handling mention in sidebar:', error);
      }
    };
    
    // Messages marked as read
    const handleMessagesRead = (data) => {
      try {
        const { channelId, userId } = data;
        
        console.log('Messages-read event received:', data);
        
        // Only update if it's the current user's read status
        if (userId === user._id) {
          console.log('Clearing unread counts for channel:', channelId);
          setUnreadCounts(prev => {
            const newCounts = {
              ...prev,
              [channelId]: {
                unreadMessages: 0,
                unreadMentions: 0
              }
            };
            console.log('New unread counts after clearing:', newCounts);
            return newCounts;
          });
        }
      } catch (error) {
        console.error('Error handling messages-read in sidebar:', error);
      }
    };
    
    // New direct message received - set up initial unread count for this channel
    const handleNewDirectMessage = (data) => {
      try {
        console.log('New direct message channel received in sidebar:', data);
        
        // Only process if this DM is for the current user
        const isForCurrentUser = user && data.channel && data.channel.members && 
          data.channel.members.some(member => 
            member._id === user._id || String(member._id) === String(user._id)
          );
        
        if (isForCurrentUser) {
          // Check if this was initiated by someone else
          const isInitiatedByOther = 
            data.initiatedBy !== user._id && 
            String(data.initiatedBy) !== String(user._id);
          
          // Only add unread count if initiated by someone else and not currently selected
          if (isInitiatedByOther && (!selectedChannel || selectedChannel._id !== data.channel._id)) {
            console.log('Adding unread count for new DM channel:', data.channel._id);
            
            // Set initial unread count for this channel
            setUnreadCounts(prev => ({
              ...prev,
              [data.channel._id]: {
                unreadMessages: 1, // Start with 1 unread message
                unreadMentions: 0
              }
            }));
 
            // Auto-expand private channels section when receiving a new DM
            setPrivateChannelsOpen(true);
          }
        }
      } catch (error) {
        console.error('Error handling new direct message in sidebar:', error);
      }
    };
    
    // Register listeners
    socketManager.addEventListener('new-message', handleNewMessage);
    socketManager.addEventListener('mention', handleMention);
    socketManager.addEventListener('messages-read', handleMessagesRead);
    socketManager.addEventListener('new-direct-message', handleNewDirectMessage);
    
    // Clean up
    return () => {
      socketManager.removeEventListener('new-message', handleNewMessage);
      socketManager.removeEventListener('mention', handleMention);
      socketManager.removeEventListener('messages-read', handleMessagesRead);
      socketManager.removeEventListener('new-direct-message', handleNewDirectMessage);
    };
  }, [user, selectedChannel, privateChannels]);

  const handleTeamChannelsToggle = () => {
    setTeamChannelsOpen(!teamChannelsOpen);
  };

  const handlePrivateChannelsToggle = () => {
    setPrivateChannelsOpen(!privateChannelsOpen);
  };

  const handleLockedChannelsToggle = () => {
    setLockedChannelsOpen(!lockedChannelsOpen);
  };

  const handleOpenUserDialog = () => {
    setUserDialogOpen(true);
  };

  const handleCloseUserDialog = () => {
    setUserDialogOpen(false);
  };

  const handleSelectUser = (selectedUser) => {
    if (onCreateDirectMessage && selectedUser) {
      onCreateDirectMessage(selectedUser);
    }
    setUserDialogOpen(false);
  };

  const isChannelSelected = (channel) => {
    return selectedChannel && selectedChannel._id === channel._id;
  };

  const getUnreadCount = (channelId) => {
    if (!channelId) {
      return { unreadMessages: 0, unreadMentions: 0 };
    }
    return unreadCounts[channelId] || { unreadMessages: 0, unreadMentions: 0 };
  };

  // Function to handle saving the new channel order
  const saveChannelOrder = async (reorderedTeamChannels) => {
    try {
      setIsReordering(true);
      // Create an array of channel IDs and their new orders
      const channelOrders = reorderedTeamChannels.map((channel, index) => ({
        channelId: channel._id,
        order: index
      }));
      
      // Send the order to the server
      await channelApi.updateChannelsOrder(channelOrders);
      
      // If we have a callback to inform the parent component, call it
      if (onChannelsReordered) {
        onChannelsReordered(reorderedTeamChannels);
      }
      
    } catch (error) {
      console.error('Error saving channel order:', error);
    } finally {
      setIsReordering(false);
      setIsDraggable(false); // Exit draggable mode after saving
    }
  };

  // Handle drag end event
  const handleDragEnd = (result) => {
    console.log('Drag ended with result:', result);
    
    // If dropped outside the list, do nothing
    if (!result.destination) {
      console.log('Dropped outside list - ignoring');
      return;
    }
    
    // Get the start and end indices
    const startIndex = result.source.index;
    const endIndex = result.destination.index;
    
    // Don't update if nothing changed
    if (startIndex === endIndex) {
      console.log('Start and end positions are the same - ignoring');
      return;
    }
    
    console.log(`Moving channel from position ${startIndex} to ${endIndex}`);
    
    // Create a copy of the team channels array
    const reorderedTeamChannels = [...teamChannels];
    
    // Reorder the array
    const [movedChannel] = reorderedTeamChannels.splice(startIndex, 1);
    reorderedTeamChannels.splice(endIndex, 0, movedChannel);
    
    console.log('New order:', reorderedTeamChannels.map(c => c.name).join(', '));
    
    // Save the new order
    saveChannelOrder(reorderedTeamChannels);
  };

  // Toggle draggable mode
  const toggleDraggable = () => {
    setIsDraggable(!isDraggable);
  };

  // Handle long press start for channel items (admin only)
  const handleChannelPressStart = (channelId, event) => {
    if (!user || !user.isAdmin) return;
    
    // Clear any existing timer
    if (longPressTimer) {
      clearTimeout(longPressTimer);
    }
    
    // Set the pressed channel and start a timer
    setPressedChannelId(channelId);
    
    // Start a timer for long press (600ms)
    const timer = setTimeout(() => {
      // Activate drag mode after long press
      setIsDraggable(true);
      setPressedChannelId(null);
      // Add visual feedback
      if (event.target) {
        event.target.style.transition = 'transform 0.2s ease';
        event.target.style.transform = 'scale(1.02)';
        setTimeout(() => {
          if (event.target) {
            event.target.style.transform = 'scale(1)';
          }
        }, 200);
      }
    }, 600);
    
    setLongPressTimer(timer);
  };
  
  // Handle press end (mouseup/touchend)
  const handleChannelPressEnd = () => {
    // Clear the timer if press ends before timeout
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    setPressedChannelId(null);
  };
  
  // Exit drag mode when done
  const exitDragMode = () => {
    setIsDraggable(false);
  };

  const renderSidebarContent = () => (
    <>
    
            {/* Empty space for mobile devices needed because of navbar overlapping the top of the sidebar */}
            <Box 
              sx={{ 
                height: '50px', 
                display: { xs: 'block', sm: 'none' } // Only visible on mobile (xs)
              }} 
            />

      <Box sx={{ 
        p: 2, 
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(10px)',
        display: 'block',  // Show on all screen sizes
        pt: { xs: 3, sm: 2 } // Add extra top padding on mobile
      }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            size="medium"
            fullWidth
            startIcon={<AddIcon />}
            onClick={onCreateChannelClick}
            sx={{ 
              mt: 1, 
              borderRadius: 1,
              boxShadow: 1,
              py: 1,
              height: { xs: '40px', sm: 'auto' }
            }}
          >
          </Button>
          
          {/* Admin Action Dropdown - Only visible to admins */}
          {user && user.isAdmin && (
            <AdminActionsMenu 
              onInviteClick={onInviteClick}
              onRemoveClick={onRemoveClick}
            />
          )}
        </Box>
      </Box>

      <List 
        dense 
        sx={{ 
          flex: 1, 
          overflowY: 'auto',
          height: { 
            xs: 'calc(100% - 80px)', // Mobile - account for padding
            sm: 'calc(100% - 80px)'   // Desktop - account for header
          },
          maxHeight: 'calc(100vh - 140px)', // Ensure it doesn't extend beyond viewport minus navbar and fixed button
          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(18, 18, 18, 0.9)' : 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(5px)',
          pb: { xs: 8, sm: 0 }, // Extra padding at bottom on mobile to prevent content being hidden by fixed button
        }}
      >
        
        {/* Team Channels Section */}
        <ListItem 
          disablePadding
          sx={{ 
            mt: 1,
            px: 2,
            display: 'flex',
            justifyContent: 'space-between'
          }}
        >
          <ListItemButton 
            onClick={handleTeamChannelsToggle}
            sx={{
              borderRadius: 1.5,
              flex: 1,
              '&:hover': {
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <ChatIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText 
              primary={
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('comms.channels')}
                </Typography>
              } 
            />
            {teamChannelsOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          
          {/* Show indicator text when in drag mode */}
          {isDraggable && (
            <Tooltip title={t('comms.finishReordering')}>
              <IconButton 
                size="small" 
                onClick={exitDragMode}
                color="primary"
                sx={{ ml: 1 }}
              >
                <DragIndicatorIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </ListItem>

        <Collapse in={teamChannelsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding dense>

            {isReordering && (
              <Box sx={{ py: 1, px: 2, textAlign: 'center' }}>
                <CircularProgress size={20} />
              </Box>
            )}
            
            {/* Admin hint for long press when not in drag mode */}
            {user && user.isAdmin && teamChannels.length > 1 && !isDraggable && !isReordering && (
              <Box 
                sx={{ 
                  py: 0.5, 
                  px: 2, 
                  mx: 2,
                  mb: 1,
                  textAlign: 'center'
                }}
              >
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ 
                    fontSize: '0.7rem',
                    fontStyle: 'italic',
                    display: 'block'
                  }}
                >
                  {t('comms.longPressToReorder')}
                </Typography>
              </Box>
            )}
            
            {isDraggable && !isReordering && (
              <Box sx={{ 
                py: 1, 
                px: 2, 
                textAlign: 'center',
                bgcolor: 'action.hover',
                borderRadius: 1,
                mx: 2,
                mb: 1
              }}>
                <Typography variant="caption" color="primary" fontWeight="bold">
                  {t('comms.dragToReorder')}
                </Typography>
              </Box>
            )}
            
            {teamChannels.length > 0 ? (
              <DragDropContext onDragEnd={handleDragEnd}>
                <StrictModeDroppable droppableId="team-channels" isDropDisabled={!isDraggable}>
                  {(provided, snapshot) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      style={{
                        transition: 'background-color 0.2s ease',
                        backgroundColor: snapshot.isDraggingOver ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                        borderRadius: '8px',
                        padding: snapshot.isDraggingOver ? '4px 0' : 0
                      }}
                    >
                      {teamChannels.map((channel, index) => {
                        const { unreadMentions } = getUnreadCount(channel._id);
                        
                        return (
                          <Draggable 
                            key={channel._id} 
                            draggableId={channel._id} 
                            index={index}
                            isDragDisabled={!isDraggable}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                style={{
                                  ...provided.draggableProps.style,
                                  opacity: snapshot.isDragging ? 0.85 : 1,
                                  transform: provided.draggableProps.style?.transform,
                                  transition: `${provided.draggableProps.style?.transition || ''}, box-shadow 0.2s ease`,
                                  zIndex: snapshot.isDragging ? 9999 : 'auto'
                                }}
                              >
                                <ListItem 
                                  disablePadding 
                                  sx={{ 
                                    px: 2,
                                    backgroundColor: snapshot.isDragging ? 'action.hover' : 
                                      pressedChannelId === channel._id ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                                    borderRadius: 1.5,
                                    boxShadow: snapshot.isDragging ? 2 : 0,
                                    mb: 0.5,
                                    border: snapshot.isDragging ? '1px solid' : 'none',
                                    borderColor: 'primary.main',
                                  }}
                                >
                                  <ListItemButton 
                                    onClick={() => !isDraggable && onChannelClick(channel)}
                                    selected={isChannelSelected(channel)}
                                    onMouseDown={(e) => handleChannelPressStart(channel._id, e)}
                                    onMouseUp={handleChannelPressEnd}
                                    onMouseLeave={handleChannelPressEnd}
                                    onTouchStart={(e) => handleChannelPressStart(channel._id, e)}
                                    onTouchEnd={handleChannelPressEnd}
                                    onTouchCancel={handleChannelPressEnd}
                                    sx={{ 
                                      pl: 4,
                                      borderRadius: 1.5,
                                      mb: 0.5,
                                      '&.Mui-selected': {
                                        bgcolor: (theme) => theme.palette.mode === 'dark' 
                                          ? 'rgba(144, 202, 249, 0.16)'
                                          : 'rgba(33, 150, 243, 0.12)',
                                        '&:hover': {
                                          bgcolor: (theme) => theme.palette.mode === 'dark' 
                                            ? 'rgba(144, 202, 249, 0.2)'
                                            : 'rgba(33, 150, 243, 0.16)',
                                        }
                                      },
                                      cursor: isDraggable ? 'move' : 'pointer',
                                      display: 'flex',
                                      alignItems: 'center'
                                    }}
                                  >
                                    {isDraggable && (
                                      <Box
                                        {...provided.dragHandleProps}
                                        sx={{ 
                                          mr: 1, 
                                          display: 'flex', 
                                          alignItems: 'center',
                                          cursor: 'grab',
                                          '&:hover': {
                                            color: 'primary.main'
                                          },
                                          '&:active': {
                                            cursor: 'grabbing',
                                            color: 'primary.main'
                                          }
                                        }}
                                      >
                                        <DragIndicatorIcon fontSize="small" />
                                      </Box>
                                    )}
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                      <TagIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText 
                                      primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          <Typography variant="body2" sx={{ mr: 1 }}>
                                            {channel.name}
                                          </Typography>
                                          {unreadMentions > 0 && (
                                            <Chip 
                                              label={unreadMentions} 
                                              color="primary" 
                                              size="small"
                                              sx={{ 
                                                height: 20, 
                                                fontSize: '0.7rem',
                                                fontWeight: 'bold',
                                                minWidth: '24px',
                                                '& .MuiChip-label': { px: 1 },
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                              }}
                                            />
                                          )}
                                        </Box>
                                      } 
                                    />
                                  </ListItemButton>
                                </ListItem>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {/* Add ghost placeholder styles to the provided placeholder */}
                      <Box sx={{ position: 'relative' }}>
                        {provided.placeholder && React.cloneElement(
                          provided.placeholder,
                          {
                            style: {
                              ...provided.placeholder.props.style,
                              backgroundColor: 'rgba(25, 118, 210, 0.2)',
                              border: '1px dashed',
                              borderColor: 'primary.main',
                              borderRadius: '8px',
                              margin: '4px 0',
                              minHeight: '40px',
                              visibility: snapshot.isDraggingOver ? 'visible' : 'hidden'
                            }
                          }
                        )}
                      </Box>
                    </div>
                  )}
                </StrictModeDroppable>
              </DragDropContext>
            ) : (
              <ListItem sx={{ pl: 4 }}>
                <ListItemText 
                  primary={
                    <Typography variant="body2" color="text.secondary">
                      {t('comms.noChannels')}
                    </Typography>
                  } 
                />
              </ListItem>
            )}
          </List>
        </Collapse>

        {/* Locked Channels Section */}
        <ListItem 
          disablePadding
          sx={{ 
            mt: 1,
            px: 2,
          }}
        >
          <ListItemButton 
            onClick={handleLockedChannelsToggle}
            sx={{
              borderRadius: 1.5,
              '&:hover': {
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <LockIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText 
              primary={
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('comms.lockedChannels')}
                </Typography>
              } 
            />
            {lockedChannelsOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>

        <Collapse in={lockedChannelsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding dense>
            {lockedChannels.length > 0 ? (
              lockedChannels.map((channel) => {
                // Check for unread mentions for locked channels
                const { unreadMessages, unreadMentions } = getUnreadCount(channel._id || '');
                
                // Skip invalid channels
                if (!channel || !channel._id) {
                  console.error('Invalid channel in lockedChannels:', channel);
                  return null;
                }
                
                return (
                <ListItem key={channel._id} disablePadding sx={{ px: 2 }}>
                  <ListItemButton 
                    onClick={() => onChannelClick(channel)}
                    selected={isChannelSelected(channel)}
                    sx={{ 
                      pl: 4,
                      borderRadius: 1.5,
                      mb: 0.5,
                      '&.Mui-selected': {
                        bgcolor: (theme) => theme.palette.mode === 'dark' 
                          ? 'rgba(144, 202, 249, 0.16)'
                          : 'rgba(33, 150, 243, 0.12)',
                        '&:hover': {
                          bgcolor: (theme) => theme.palette.mode === 'dark' 
                            ? 'rgba(144, 202, 249, 0.2)'
                            : 'rgba(33, 150, 243, 0.16)',
                        }
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LockIcon fontSize="small" />
                      </Box>
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ mr: 1 }}>
                            {channel.name}
                          </Typography>
                          {unreadMentions > 0 && (
                            <Chip 
                              label={unreadMentions} 
                              color="primary" 
                              size="small"
                              sx={{ 
                                height: 20, 
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                                minWidth: '24px',
                                '& .MuiChip-label': { px: 1 },
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                              }}
                            />
                          )}
                        </Box>
                      } 
                    />
                  </ListItemButton>
                </ListItem>
                );
              })
            ) : (
              <ListItem sx={{ pl: 4 }}>
                <ListItemText 
                  primary={
                    <Typography variant="body2" color="text.secondary">
                      {t('comms.noLockedChannels')}
                    </Typography>
                  } 
                />
              </ListItem>
            )}
          </List>
        </Collapse>

        {/* Direct Messages Section */}
        <ListItem 
          disablePadding
          sx={{ 
            mt: 1,
            px: 2,
          }}
        >
          <ListItemButton 
            onClick={handlePrivateChannelsToggle}
            sx={{
              borderRadius: 1.5,
              '&:hover': {
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText 
              primary={
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('comms.directMessages')}
                </Typography>
              } 
            />
            {privateChannelsOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>

        <Collapse in={privateChannelsOpen} timeout="auto" unmountOnExit>
          <Box sx={{ px: 3, py: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PersonAddIcon fontSize="small" />}
              onClick={handleOpenUserDialog}
              sx={{ 
                borderRadius: 2,
                mb: 1,
                width: '100%',
              }}
            >
              {t('comms.newDirectMessage')}
            </Button>
          </Box>
          <List component="div" disablePadding dense>
            {privateChannels.length > 0 ? (
              privateChannels.map((channel) => {
                // For DMs, show the other user's name, not the channel name
                const otherUser = channel.members?.find(member => 
                  member?._id !== user?._id
                );
                const displayName = otherUser ? 
                  (otherUser.name || otherUser.username) : 
                  channel.name || 'Unknown';
                  
                // Get both unread messages and mentions
                const { unreadMessages, unreadMentions } = getUnreadCount(channel._id || '');
                
                // Safety check - skip rendering if channel is invalid
                if (!channel || !channel._id) {
                  console.error('Invalid channel in privateChannels:', channel);
                  return null;
                }

                return (
                  <ListItem key={channel._id} disablePadding sx={{ px: 2 }}>
                    <ListItemButton 
                      onClick={() => onChannelClick(channel)}
                      selected={isChannelSelected(channel)}
                      sx={{ 
                        pl: 4, 
                        borderRadius: 1.5,
                        mb: 0.5,
                        '&.Mui-selected': {
                          bgcolor: (theme) => theme.palette.mode === 'dark' 
                            ? 'rgba(144, 202, 249, 0.16)'
                            : 'rgba(33, 150, 243, 0.12)',
                          '&:hover': {
                            bgcolor: (theme) => theme.palette.mode === 'dark' 
                              ? 'rgba(144, 202, 249, 0.2)'
                              : 'rgba(33, 150, 243, 0.16)',
                          }
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Badge
                          color="primary"
                          badgeContent={unreadMessages}
                          invisible={unreadMessages === 0}
                          sx={{ 
                            '& .MuiBadge-badge': { 
                              fontSize: '0.7rem',
                              minWidth: '18px',
                              height: '18px'
                            } 
                          }}
                        >
                          {otherUser && otherUser.imageUrl ? (
                            <Avatar
                              src={otherUser.imageUrl}
                              alt={otherUser.name || otherUser.username || 'User'}
                              sx={{ width: 24, height: 24 }}
                            >
                              {(otherUser.name || otherUser.username || 'U')[0]}
                            </Avatar>
                          ) : (
                            <PersonIcon fontSize="small" />
                          )}
                        </Badge>
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography 
                              variant="body2" 
                              fontWeight={unreadMessages > 0 ? 'bold' : 'normal'}
                              noWrap
                              sx={{ mr: 1, maxWidth: 120 }}
                            >
                              {displayName}
                            </Typography>
                            {unreadMentions > 0 && (
                              <Chip 
                                label={unreadMentions} 
                                color="primary" 
                                size="small"
                                sx={{ 
                                  height: 20, 
                                  fontSize: '0.7rem',
                                  fontWeight: 'bold',
                                  minWidth: '24px',
                                  '& .MuiChip-label': { px: 1 },
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                }}
                              />
                            )}
                          </Box>
                        } 
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })
            ) : (
              <ListItem sx={{ pl: 4 }}>
                <ListItemText 
                  primary={
                    <Typography variant="body2" color="text.secondary">
                      {t('comms.noDirectMessages')}
                    </Typography>
                  } 
                />
              </ListItem>
            )}
          </List>
        </Collapse>
      </List>

      {/* User Selection Dialog */}
      <UserSelectionDialog
        open={userDialogOpen}
        onClose={handleCloseUserDialog}
        onSelectUser={handleSelectUser}
        currentUserId={user?._id}
        privateChannels={privateChannels}
      />
    </>
  );

  return isMobile ? (
    <Drawer
      variant="temporary"
      open={visible}
      onClose={onCloseSidebar}
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: SIDEBAR_WIDTH,
          boxSizing: 'border-box',
          borderRight: 'none',
          boxShadow: 3,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          pt: 1 // Add top padding
        }
      }}
    >
      {renderSidebarContent()}
    </Drawer>
  ) : (
    <Box
      sx={{
        width: visible ? SIDEBAR_WIDTH : 0,
        flexShrink: 0,
        borderRight: (theme) => `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
        transition: (theme) => theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        boxShadow: '0 0 10px rgba(0,0,0,0.05)',
      }}
    >
      {renderSidebarContent()}
    </Box>
  );
};

export default ChannelSidebar; 